import { useCallback, useRef, useState } from 'react'
import { skipToken, useQuery } from '@apollo/client/react'

import { SelectableTeamMembersQuery } from './selectableTeamMembersQuery'
import type {
  SelectableTeamMember,
  TeamMemberScope,
} from './selectableTeamMembersQuery'

type Result = {
  members: SelectableTeamMember[]
  loading: boolean
  error: boolean
  hasNextPage: boolean
  loadingMore: boolean
  loadMore: () => Promise<void>
}

export function useSelectableTeamMembers(
  scope: TeamMemberScope | null,
  search: string,
): Result {
  const selectionKey = scope ? JSON.stringify([scope, search]) : null
  const selectionRef = useRef(selectionKey)
  selectionRef.current = selectionKey
  const [pagingKey, setPagingKey] = useState<string | null>(null)
  const [pageErrorKey, setPageErrorKey] = useState<string | null>(null)
  const query = useQuery(
    SelectableTeamMembersQuery,
    scope
      ? {
          variables: { input: { scope, search }, first: 10, after: null },
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: true,
        }
      : skipToken,
  )
  const connection =
    scope && query.dataState === 'complete'
      ? query.data.selectableTeamMembers
      : undefined
  const members: SelectableTeamMember[] = []
  const seen = new Set<string>()
  for (const { node } of connection?.edges ?? []) {
    if (seen.has(node.id)) continue
    seen.add(node.id)
    members.push(node)
  }
  const endCursor = connection?.pageInfo.endCursor
  const hasNextPage = Boolean(connection?.pageInfo.hasNextPage && endCursor)
  const loadMore = useCallback(async (): Promise<void> => {
    if (!scope || !selectionKey || !endCursor || !hasNextPage) return
    if (query.loading || pagingKey === selectionKey) return
    setPagingKey(selectionKey)
    setPageErrorKey(null)
    try {
      await query.fetchMore({
        variables: { input: { scope, search }, first: 10, after: endCursor },
        updateQuery(previous, { fetchMoreResult }) {
          if (!fetchMoreResult || selectionRef.current !== selectionKey)
            return previous
          return {
            selectableTeamMembers: {
              ...fetchMoreResult.selectableTeamMembers,
              edges: [
                ...previous.selectableTeamMembers.edges,
                ...fetchMoreResult.selectableTeamMembers.edges,
              ],
            },
          }
        },
      })
    } catch {
      if (selectionRef.current === selectionKey) setPageErrorKey(selectionKey)
    } finally {
      setPagingKey((current) => (current === selectionKey ? null : current))
    }
  }, [scope, selectionKey, endCursor, hasNextPage, query, pagingKey, search])

  if (!scope) {
    return {
      members: [],
      loading: false,
      error: false,
      hasNextPage: false,
      loadingMore: false,
      loadMore,
    }
  }

  return {
    members,
    loading: Boolean(scope && query.loading && !connection),
    error: Boolean(query.error || pageErrorKey === selectionKey),
    hasNextPage,
    loadingMore: pagingKey === selectionKey,
    loadMore,
  }
}
